import { MouseEvent, useState, useRef, useEffect } from 'react';
import { MessageCircle, Repeat, Zap, Quote, X, Bookmark, Gift } from 'lucide-react';
import { formatNumber } from '@/utils/formatNumber';
import { useLike } from '@/hooks/useToggleService';
import { useActivityTracking } from '@/hooks/useActivityTracking';

interface PostActionsProps {
  postId: string;
  initialLikeCount: number;
  onCommentClick: () => void;
  onRepost: (content: string) => void;
  commentCount?: number;
  repostCount?: number;
  postTitle?: string;
  postThumbnail?: string;
  // Gift props
  onGiftClick?: () => void;
  // Bookmark props
  onBookmarkClick?: () => void;
  isBookmarked?: boolean;
  bookmarkCount?: number;
  bookmarkLoading?: boolean;
}

/**
 * Component for post interaction actions (like, comment, share, repost)
 */
export default function PostActions({
  postId,
  initialLikeCount,
  onCommentClick,
  onRepost,
  commentCount,
  repostCount = 0, // Default repost count if not provided
  postTitle,
  postThumbnail,
  onGiftClick,
  onBookmarkClick,
  isBookmarked,
  bookmarkCount,
  bookmarkLoading
}: PostActionsProps) {
  const { isLiked, toggleLike, likeCount } = useLike(postId, initialLikeCount);
  const { trackPostInteraction } = useActivityTracking();
  const [showRepostModal, setShowRepostModal] = useState(false);
  const repostModalRef = useRef<HTMLDivElement>(null);

  // Close repost modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (repostModalRef.current && !repostModalRef.current.contains(event.target as Node)) {
        setShowRepostModal(false);
      }
    };

    if (showRepostModal) {
      document.addEventListener('mousedown', handleClickOutside as any);
      return () => document.removeEventListener('mousedown', handleClickOutside as any);
    }
  }, [showRepostModal]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showRepostModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showRepostModal]);

  const closeRepostModal = () => {
    setShowRepostModal(false);
  };

  const handleRepostOption = (type: 'quote' | 'simple') => {
    setShowRepostModal(false);
    onRepost?.(type);
  };

  return (
    <>
      {/* Left side actions: Like, Comment, Repost */}
      <div className="flex items-center gap-6">
        {/* Like Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            toggleLike();
            // Track like interaction
            trackPostInteraction(postId, 'like', isLiked ? 'unliked' : 'liked', postTitle, postThumbnail);
          }}
          className={`flex items-center gap-1.5 transition-all duration-200 group ${
            isLiked 
              ? 'text-[#ffc11f]' 
              : 'text-gray-600 dark:text-gray-400 hover:text-[#ffc11f]'
          }`}
        >
          <div className="relative">
            <Zap className={`w-5 h-5 transition-all duration-200 ${
              isLiked 
                ? 'fill-current scale-110' 
                : 'group-hover:scale-110'
            }`} />
          </div>
          <span className="text-sm font-medium">
            {formatNumber(likeCount)}
          </span>
        </button>

        {/* Comment Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onCommentClick();
            // Track comment click (opening comment section)
            trackPostInteraction(postId, 'view', 'opened comments', postTitle, postThumbnail);
          }}
          className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-500 transition-colors duration-200 group"
        >
          <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          <span className="text-sm font-medium">
            {formatNumber(commentCount || 0)}
          </span>
        </button>

        {/* Repost Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setShowRepostModal(true);
          }}
          className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 hover:text-green-500 transition-colors duration-200 group"
        >
          <Repeat className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          <span className="text-sm font-medium">
            {formatNumber(repostCount || 0)}
          </span>
        </button>
      </div>

      {/* Right side actions: Gift, Bookmark */}
      <div className="flex items-center gap-2">
        {/* Gift Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onGiftClick?.();
            // Track gift interaction
            trackPostInteraction(postId, 'view', 'gift clicked', postTitle, postThumbnail);
          }}
          className="p-2 rounded-full hover:bg-[#065900] hover:text-white transition-colors"
        >
          <Gift size={20} />
        </button>

        {/* Bookmark Button */}
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onBookmarkClick?.();
              // Track bookmark interaction
              trackPostInteraction(postId, 'view', 'bookmark clicked', postTitle, postThumbnail);
            }}
            disabled={bookmarkLoading}
            className={`p-2 rounded-full transition-colors ${
              isBookmarked 
                ? 'bg-[#ffc11f] text-white' 
                : 'hover:bg-[#065900] hover:text-white'
            } ${bookmarkLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Bookmark size={20} />
          </button>
          {bookmarkCount && bookmarkCount > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatNumber(bookmarkCount)}
            </span>
          )}
        </div>
      </div>

      {/* Repost Modal */}
      {showRepostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={repostModalRef} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Repost</h3>
              <button 
                onClick={closeRepostModal}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={() => {
                  handleRepostOption('simple');
                  // Track simple repost
                  trackPostInteraction(postId, 'share', 'simple repost', postTitle, postThumbnail);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Repeat className="w-5 h-5 text-green-500" />
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Repost</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Instantly share to your timeline</div>
                </div>
              </button>
              
              <button 
                onClick={() => {
                  handleRepostOption('quote');
                  // Track quote repost  
                  trackPostInteraction(postId, 'share', 'quote repost', postTitle, postThumbnail);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Quote className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Quote Tweet</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Add a comment</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
